import FileUpload from "./base/FileUpload.jsx";
import { useState } from "react";
import { theme } from "../theme.js";

export default function FileUploadLicense({
  licenseRequirement,
  uploadUrl,
  onUploaded,
  onChange,
  initialValues = null,
  existingValues = null,
  required = false,
  onExistingDeleted = null,
}) {
  // Initialise from saved state if available
  const [uploadedDocs, setUploadedDocs] = useState(initialValues || {});

  const documents = licenseRequirement.documents || [];
  const minRequired = licenseRequirement.min_required || 1;
  const uploadedCount = Object.keys(uploadedDocs).length;
  // Also count existing server docs that haven't been replaced yet
  const existingCount = existingValues
    ? Object.keys(existingValues).filter((k) => !uploadedDocs[k]).length
    : 0;
  const isComplete = (uploadedCount + existingCount) >= minRequired;

  const handleFileUploaded = (docName, json) => {
    setUploadedDocs((prev) => ({
      ...prev,
      [docName]: json,
    }));

    // Notify parent of all uploaded documents
    onUploaded({
      ...uploadedDocs,
      [docName]: json,
    });
  };

  const handleFileRemoved = (docName) => {
    setUploadedDocs((prev) => {
      const newDocs = { ...prev };
      delete newDocs[docName];
      return newDocs;
    });

    onChange(null);
  };

  const showMissing = required && !isComplete;

  return (
    <div style={{ ...styles.container, ...(showMissing ? styles.containerMissing : {}) }}>
      <div style={styles.header}>
        <div style={styles.titleRow}>
          <h3 style={styles.title}>
            {licenseRequirement.name}
            {required && <span style={styles.requiredStar}>*</span>}
          </h3>
          <span style={styles.progressLabel}>
            {uploadedCount + existingCount}/{minRequired}
            {minRequired === 1 ? " requis" : " requis"}
          </span>
        </div>
        <div style={styles.progressBar}>
          <div
            style={{
              ...styles.progress,
              width: `${Math.min(((uploadedCount + existingCount) / minRequired) * 100, 100)}%`,
            }}
          />
        </div>
      </div>

      <div style={styles.documentsGrid}>
        {documents.map((docName) => (
          <FileUpload
            key={docName}
            requirement={{ id_bdd: docName, name: docName }}
            accept="application/pdf,image/*"
            uploadImmediately={true}
            uploadUrl={uploadUrl}
            initialValue={uploadedDocs[docName] || null}
            existingFile={existingValues?.[docName] || null}
            onUploaded={(json) => handleFileUploaded(docName, json)}
            onExistingDeleted={(existingFileData) => onExistingDeleted && onExistingDeleted(existingFileData)}
            onChange={(file) => {
              if (!file) {
                handleFileRemoved(docName);
              }
            }}
          />
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: theme.spacing.lg,
    backgroundColor: "#f9fafb",
    borderRadius: theme.radius.lg,
    border: `1px solid ${theme.colors.border}`,
    marginBottom: theme.spacing.lg,
  },
  containerMissing: {
    border: `2px solid ${theme.colors.error || "#e53e3e"}`,
    backgroundColor: "rgba(229,62,62,0.04)",
  },
  requiredStar: {
    color: theme.colors.error || "#e53e3e",
    fontWeight: "bold",
    fontSize: "1.1em",
    marginLeft: "4px",
    lineHeight: 1,
  },
  header: {
    marginBottom: theme.spacing.md,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing.sm,
  },
  title: {
    margin: 0,
    fontSize: theme.fonts.size.lg,
    fontWeight: theme.fonts.weight.bold,
    color: theme.colors.textInverted,
  },
  progressLabel: {
    fontSize: theme.fonts.size.xs,
    color: theme.colors.textSecondary,
    whiteSpace: "nowrap",
  },
  progressBar: {
    width: "100%",
    height: "6px",
    backgroundColor: "#e5e7eb",
    borderRadius: "3px",
    overflow: "hidden",
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  progress: {
    height: "100%",
    backgroundColor: "#10b981",
    transition: "width 0.3s ease",
  },
  documentsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: theme.spacing.md,
  },
  successMessage: {
    marginTop: theme.spacing.md,
    padding: theme.spacing.md,
    backgroundColor: "#d1fae5",
    color: "#065f46",
    borderRadius: theme.radius.md,
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.semibold,
    textAlign: "center",
  },
};
